import React, { useEffect, useMemo, useRef, useState } from "react";
import { Power, Search } from "lucide-react";
import { useWindows } from "../WindowManager";
import { QUICK_FILES } from "../fs";
import { NodeIcon } from "../fileIcons";
import { PROFILE } from "../data";
import "./StartMenu.css";

/** Recently used documents, the way Start lists real files. */
const RECOMMENDED = [
  { file: QUICK_FILES.readme, meta: "Desktop" },
  { file: QUICK_FILES.resume, meta: "Documents" },
  { file: QUICK_FILES.about, meta: "Documents \\ Portfolio" },
  { file: QUICK_FILES.experience, meta: "Documents \\ Portfolio" },
];

export default function StartMenu({ open: isOpen, onClose, onLock }) {
  const { apps, open } = useWindows();
  const [query, setQuery] = useState("");
  const panelRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!isOpen) {
      setQuery("");
      return;
    }
    const t = setTimeout(() => inputRef.current?.focus(), 120);
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => {
      clearTimeout(t);
      window.removeEventListener("keydown", onKey);
    };
  }, [isOpen, onClose]);

  const results = useMemo(() => {
    const listed = apps.filter((a) => !a.hidden);
    const q = query.trim().toLowerCase();
    if (!q) return listed;
    return listed.filter(
      (a) =>
        a.title.toLowerCase().includes(q) ||
        (a.subtitle || "").toLowerCase().includes(q)
    );
  }, [apps, query]);

  if (!isOpen) return null;

  const launch = (id) => {
    open(id);
    onClose();
  };

  return (
    <>
      <div className="start-scrim" onPointerDown={onClose} />
      <div
        className="start-menu"
        ref={panelRef}
        role="dialog"
        aria-label="Start menu"
      >
        <div className="start-search">
          <Search size={16} />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && results[0]) launch(results[0].id);
            }}
            placeholder="Search for apps, settings and documents"
            aria-label="Search"
          />
        </div>

        <div className="start-body win-scroll">
          <div className="start-section-head">
            <span>{query ? "Results" : "Pinned"}</span>
            {!query && <span className="start-section-head__hint">All apps</span>}
          </div>

          <div className="start-grid">
            {results.map((app) => (
              <button
                key={app.id}
                className="start-tile"
                onClick={() => launch(app.id)}
              >
                <span className="start-tile__icon" style={{ "--tile": app.color }}>
                  <app.icon size={22} strokeWidth={1.9} />
                </span>
                <span className="start-tile__label">{app.title}</span>
              </button>
            ))}
            {!results.length && (
              <p className="start-empty">No results for “{query}”.</p>
            )}
          </div>

          {!query && (
            <>
              <div className="start-section-head">
                <span>Recommended</span>
              </div>
              <div className="start-reco">
                {RECOMMENDED.map(({ file, meta }) => (
                  <button
                    key={file.id}
                    className="reco-item"
                    onClick={() => {
                      open(file.open.app, { fileId: file.id, ...file.open });
                      onClose();
                    }}
                  >
                    <span className="reco-item__icon reco-item__icon--file">
                      <NodeIcon node={file} size={18} />
                    </span>
                    <span className="reco-item__body">
                      <span className="reco-item__title">{file.name}</span>
                      <span className="reco-item__meta">{meta}</span>
                    </span>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="start-footer">
          <button className="start-user" onClick={() => launch("settings")}>
            <img src={PROFILE.avatar} alt="" className="start-user__avatar" />
            <span>{PROFILE.name}</span>
          </button>
          <button
            className="start-power"
            onClick={onLock}
            aria-label="Lock"
            title="Lock"
          >
            <Power size={17} />
          </button>
        </div>
      </div>
    </>
  );
}
